import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Card, Tabs, Divider, Col, Row, Spin, Modal, Space } from 'antd';
import { useSearchParams } from 'react-router-dom';
import Button from '@mui/material/Button';
import TickerSearch from '../../modules/Search/TickerSearch/TickerSearch';
import StockChart from "../../charts/StockChart";
import PerformanceHistory from './Views/PerformanceHistory';
import Risk from './Views/Risk';
import {
    prepareOverviewChartData
} from './utils';
import ItemController from "./components/ItemController";
import ItemMenu from "./components/ItemMenu";
import PortfolioSearch from "../../modules/Search/PortfolioSearch/PortfolioSearch";
import HistoricalVolatility from "./Views/HistoricalVolatility";
import ReturnsDistributions from "./Views/ReturnsDistributions";
import * as PropTypes from "prop-types";
import Placeholder from "../../components/Placeholder";
import GeneralStockChart from "../../SuperComponents/StockCharts/GeneralStockChart";
import {useRelativeMetrics} from "../../SuperComponents/StockCharts/useRelativeMetrics";
import RelativeMetricToolbar from "../../SuperComponents/StockCharts/RelativeMetricToolbar";
import { useScreenHeight } from "../../hooks/useScreen";
import usePerformanceComparison from "./usePerformanceComparison";
import NavTrail from "../../layouts/NavTrail";
import Drawdowns from "./Views/Drawdowns";
import { isUUID } from "../../utils/strings";
import { History, Delete, Search } from 'lucide-react';
import PerformanceComparisonHistory from "./PerformanceComparisonHistory";
import {MenuOpenRounded, MoreHorizRounded} from "@mui/icons-material";
const { TabPane } = Tabs;

const tabKeyToLabelMap = {
  "1": "Overview",
  "2": "Returns",
  "3": "Risk",
  "6": "Volatility",
  "7": "Distributions",
  "8": "Metrics",
};




/**
 *
 * @param initialObjects {Array[Object[{id, label}]], optional} - An array of objects containing id and label of the initial
 *                                                                ...timeseries portfolios/securities to load
 * @param extraItems {Array[Object[{id, label}]], optional} - An array of objects containing id and label of the initial
 * @returns {JSX.Element}
 * @constructor
 */
const PerformanceComparison=({initialObjects, extraItems, benchmark, isChild=false})=>{
    const [searchParams] = useSearchParams();

  const {items, commonStartDate, loading, itemColors} = useSelector(state => state.comparison);
  const maxTickers = useSelector(state=>state.appStates.comparison?._maxTickers);
  const [showItemController, setShowItemController] = useState(false);
  const relativeMetrics = useRelativeMetrics({benchmark});
  const screenHeight = useScreenHeight();
  const {
    showSearch,
    setShowSearch,
    showHistory,
    toggleHistory,
    selectedExtraItems,
    activeTab,
    setActiveTab,
    handleTickersSubmit,
    handlePortfolioSubmit,
    handleRemoveItem,
    toggleExtraItems,
    reset,
    mapColorsToLabels,
  } = usePerformanceComparison({ initialObjects, benchmark, relativeMetrics });

  useEffect(() => {
    const ids = searchParams.get('ids');
    const labels = searchParams.get('labels');
    let idLabelMap = {}
    if (ids && labels && ids.split(',').length === labels.split(',').length) {
      idLabelMap = ids.split(',').reduce((acc, id, index) => {
        acc[id] = labels.split(',')[index];
        return acc;
      }, {});
    }
    if (ids && !items.length) {
      const idArray = ids.split(',');
      const tickerIds = idArray.filter(id => !isUUID(id));
      const portfolioIds = idArray.filter(id => isUUID(id));

      if (tickerIds.length) {
        handleTickersSubmit(tickerIds.map((id, index) => ({
          symbol: id,
          name: idLabelMap[id] || id
        })));
      }
      if (portfolioIds.length) {
          handlePortfolioSubmit(portfolioIds.map((id, index)=>({
            id,
            name: idLabelMap[id] || id
          })));
      }
      setShowSearch(false);
    }
  }, [searchParams]);


  if (loading){
      return (
            <Spin className={'mt-6'}>
              <div className={'p-6'}>
                Loading Metrics
              </div>
            </Spin>
      )
  }
  const navTrailConfig = [
      {label: "Comparison", path: "/comparison"},
      {label: tabKeyToLabelMap?.[activeTab] || "Overview"}
  ]

  return (
        <div>
          {!isChild && (
              <NavTrail items={navTrailConfig}
                        borderBottom={"1px solid #ededed"}
                        extraRight={<Space className={'mr-8'}>
                          <Button variant={'text'} onClick={()=>setShowSearch(true)} startIcon={<Search size={18}/>}>
                            <span className={'font-medium cursor-pointer'}>Search</span>
                          </Button>
                          <Button
                            variant="text"
                            onClick={toggleHistory}
                            startIcon={<History size={18}/>}
                          >
                            <span className={'font-medium cursor-pointer'}>History</span>
                          </Button>
                          <Button variant={'text'} onClick={reset} startIcon={<Delete size={18}/>}>
                            <span className={'font-medium cursor-pointer'}>Reset</span>
                          </Button>
                        </Space>}
              />

          )}
          {showSearch && (
              <Tabs defaultActiveKey={'tickers'} centered rootClassName={'py-3'}>
                <TabPane tab={'Search Tickers'} key={'tickers'}>
                  <TickerSearch
                      onTickersSubmit={handleTickersSubmit}
                      closeSearch={() => setShowSearch(false)}
                      maxTickers={maxTickers}
                  />
                </TabPane>
                <TabPane tab={'Select Portfolios'} key={'portfolios'}>
                  <PortfolioSearch onSelect={handlePortfolioSubmit} />
                </TabPane>
              </Tabs>
          )}
            {items.length > 0 && (
                <Tabs defaultActiveKey="1"
                      tabPosition={'left'}
                      onChange={setActiveTab}
                      size={'small'}
                      tabBarExtraContent={{
                        left: <div className={'py-6'}>
                          <Button variant={'text'} onClick={()=>setShowItemController(true)} startIcon={<MoreHorizRounded/>}>Items</Button>
                        </div>
                      }}
                >
                    <TabPane tab="Overview" key="1">
                        <Card bordered={false}>
                            <ItemController items={items}
                                itemColors={itemColors}
                                onRemove={handleRemoveItem}
                                extraItems={extraItems}
                                onToggleExtraItems={toggleExtraItems}
                                initialSelectedItems={selectedExtraItems}
                                setShowSearch={setShowSearch}
                                reset={reset}
                            />
                          <Divider/>
                          {items.length > 0 && items[0].hasOwnProperty('chart') && (
                                <StockChart dataSeries={prepareOverviewChartData(items, itemColors)} allowChartInteractions={false} height={'65vh'} />
                          )}
                        </Card>
                    </TabPane>
                    <TabPane tab="Returns" key="2">
                      <Card bordered={false}>
                          <PerformanceHistory items={items} itemColors={mapColorsToLabels}/>
                      </Card>
                    </TabPane>
                    <TabPane tab="Risk" key="3">
                        <div className={'py-4 pr-6'}>
                            <Risk items={items} itemColors={mapColorsToLabels}/>
                        </div>
                    </TabPane>
                    <TabPane tab={'Volatility'} key={'6'}>
                        <Card bordered={false}>
                            <HistoricalVolatility items={items} itemColors={itemColors}/>
                        </Card>
                    </TabPane>
                    <TabPane tab={'Distributions'} key={'7'}>
                      <ReturnsDistributions items={items} itemColors={itemColors}/>
                    </TabPane>
                    <TabPane tab={'Metrics'} key={'8'}>
                        <div>
                            {!relativeMetrics.isCurrent ? (
                              <div>
                                  <Placeholder text={'Click to load metrics'} />
                                  <Divider/>
                                  <Spin>Loading Metrics...</Spin>
                              </div>
                          ) : (
                              <Row gutter={16} align={'top'}>
                                <Col flex={'auto'} className={'py-6'}>
                                  <div className={'px-4 2xl:pl-8 xl:pl-6 pr-12'} style={{marginTop: screenHeight*0.025}}>
                                    <GeneralStockChart
                                      height={screenHeight * 0.75}
                                      dataPaths={
                                        items.map(item => {
                                          return {
                                            path: `timeSeries.metrics.${item.id}.${relativeMetrics.metric}.${relativeMetrics.targetSymbol}.${relativeMetrics.window}`,
                                            id: item.id
                                          }
                                        })
                                      }
                                      seriesConfig={items.reduce((config, item) => {
                                        config[item.id] = { dataField: 'value', yScale: 'linear' };
                                        return config;
                                      }, {})}
                                      colorMapping={itemColors}
                                      labelMapping={items.reduce((mapping, item) => {
                                        mapping[item.id] = item.label;
                                        return mapping;
                                      }, {})}
                                      stockChartProps={{
                                        allowChartInteractions: true,
                                      }}
                                    />
                                  </div>
                                </Col>
                                <Col flex={'0 0 100px'} className={'shadow-sm min-h-screen p-6'}>
                                  <RelativeMetricToolbar
                                      direction={'vertical'}
                                      metric={relativeMetrics.metric}
                                      setMetric={relativeMetrics.setMetric}
                                      targetSymbol={relativeMetrics.targetSymbol}
                                      setTargetSymbol={relativeMetrics.setTargetSymbol}
                                      window={relativeMetrics.window}
                                      setWindow={relativeMetrics.setWindow}
                                  />
                                </Col>
                            </Row>

                          )}
                        </div>
                    </TabPane>
                    {Array.isArray(items) && items?.length>0 && (
                        <TabPane tab={'Drawdowns'} key={'9'}>
                            <Drawdowns items={items}/>
                        </TabPane>
                    )}

                  </Tabs>
            )}
          <Modal onCancel={()=>setShowItemController(false)}
                 onOk={()=>setShowItemController(false)}
                 open={showItemController}
                 width={700}
          >
            <ItemMenu items={items}
                      itemColors={itemColors}
                      onRemove={handleRemoveItem}
                      extraItems={extraItems}
                      onToggleExtraItems={toggleExtraItems}
                      initialSelectedItems={selectedExtraItems}
                      setShowSearch={setShowSearch}
                      reset={reset}
            />
          </Modal>
          <Modal
            title="Comparison History"
            open={showHistory}
            onCancel={toggleHistory}
            footer={null}
            width={600}
          >
            <PerformanceComparisonHistory />
          </Modal>
        </div>
    );

}


PerformanceComparison.propTypes = {
  initialObjects: PropTypes.any,
  isChildComponent: PropTypes.bool,
  extraItems: PropTypes.any
}

PerformanceComparison.defaultProps = {initialObjects: null, isChildComponent: false, extraItems: null}

export default PerformanceComparison;
